import { IResponsePinCheck, IResponseUser } from '@/core/domain/entities/user';
import axios from 'axios';
import {
  ICompleteCheckSmsReqParams,
  IJoinRequestParams,
  ILoginReqParams,
  IPinCheckReqParams,
} from '@/core/domain/entities/request';
import { mapPinCheck, mapUser } from '@/core/domain/mapper/mapUser';
import { checklocal } from '@/utils';
import { response } from './errorCodes copy';

// 세션 체크 함수 추가
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function checkSession(): Promise<{ isLoggedIn: boolean; user?: any }> {
  try {
    const url = `${checklocal()}/member/check-session`;

    const response = await axios.get(url, { withCredentials: true });

    // user 데이터가 문자열인 경우 객체로 변환
    if (response.data.user && typeof response.data.user === 'string') {
      response.data.user = JSON.parse(response.data.user);
    }
    return response.data;
  } catch (err) {
    console.error('Session check failed:', err);
    return { isLoggedIn: false };
  }
}

/**
 * 로그인 API
 *
 * @export
 * @param {ILoginReqParams} loginData
 * @return {*}  {(Promise<number | IUser>)}
 */

export async function userLogin(
  loginData: ILoginReqParams,
): Promise<
  | { response: response; status: string; errorCount: number; message: string }
  | { response: IResponseUser; status: string; message: string }
> {
  try {
    const response = await axios.post(`${checklocal()}/member/sign`, loginData, { withCredentials: true });

    if (response.data.status !== 'error') {
      // 로그인 성공 시 세션 체크를 즉시 수행
      const sessionResponse = await checkSession();
      if (sessionResponse.isLoggedIn) {
        // 여기서 전역 상태 업데이트 함수를 호출
        return { response: mapUser(sessionResponse.user), status: 'success', message: response.data.message };
      }
    }

    return {
      response: response.data,
      status: 'error',
      errorCount: response.data.errorCount,
      message: response.data.errorCode.message,
    };
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      const errorData = err.response?.data.error;

      if (errorData?.errorCount > 0) {
        return { response: errorData, status: 'error', message: errorData.errorCode.message };
      }
      return { response: errorData, status: 'error', message: errorData.errorCode.message };
    }
    return {
      response: { status: 'error', errorCode: { code: '0000', category: '성공', message: '성공' }, result: [] },
      status: 'error',
      errorCount: 0,
      message: '성공',
    };
  }
}

/**
 * 휴대폰 번호 중복 체크 API
 *
 * @export
 * @param {string} phoneNumber
 * @return {*}  {Promise<boolean>}
 */
export async function validatePhoneNumberUniqueness(
  phone: string,
): Promise<{ status: string; errorMessage: string; isUnique: boolean }> {
  try {
    const response = await axios.post(`${checklocal()}/member/checkPhoneUnique`, { phone: phone }, { timeout: 10000 });

    if (response.data.status === 'error') {
      return { status: 'error', errorMessage: response.data.errorCode.message, isUnique: false };
    } else {
      return { status: 'success', errorMessage: '', isUnique: response.data.isUnique };
    }
  } catch (err) {
    console.error('Full error:', err);
    if (axios.isAxiosError(err)) {
      return { status: 'error', errorMessage: err.response?.data.errorCode.message, isUnique: false };
    }
    return { status: 'error', errorMessage: '휴대폰 번호 중복 체크 실패', isUnique: false };
  }
}

/**
 * 닉네임 중복 체크
 * @param {string} name
 * @returns {Promise<string>}
 */
export async function validateNicknameUniqueness(
  name: string,
): Promise<{ status: string; errorMessage: string; isUnique: boolean }> {
  try {
    const response = await axios.post(`${checklocal()}/member/checkNameUnique`, { name }, { timeout: 10000 });

    if (response.data.status === 'error') {
      return { status: 'error', errorMessage: response.data.errorCode.message, isUnique: response.data.isUnique };
    } else {
      return { status: 'success', errorMessage: '', isUnique: true };
    }
  } catch (err) {
    console.error(JSON.stringify(err));
    if (axios.isAxiosError(err)) {
      return { status: 'error', errorMessage: err.response?.data.errorCode.message, isUnique: false };
    }

    return { status: 'error', errorMessage: '닉네임 중복 체크 실패', isUnique: false };
  }
}

/**
 * SMS 인증 완료
 * @param {ICompleteCheckSmsReqParams} reqData
 * @returns {Promise<string>}
 */
export async function completeCheckSms(reqData: ICompleteCheckSmsReqParams): Promise<string> {
  try {
    const response = await axios.post(`${checklocal()}/member/sms/verify`, reqData);

    return response.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return '0000';
  }
}

/**
 * 국가 조회
 * @returns {Promise<string>}
 */
export async function getCountry(): Promise<string> {
  try {
    const response = await axios.get(`${checklocal()}/member/country`);

    return response.data;
  } catch (err) {
    console.error(err);
    return '';
  }
}

/**
 * 회원 가입
 * @param {IJoinRequestParams} reqData
 * @returns {Promise<string>}
 */
export async function join(
  reqData: IJoinRequestParams,
): Promise<{ message: string; status: string; errorCode?: string }> {
  try {
    const response = await axios.post(`${checklocal()}/member/join`, reqData);
    console.log('response', response);

    if (response.data.status === 'error') {
      return {
        message: response.data.errorCode.message,
        status: response.data.status,
        errorCode: response.data.errorCode.code,
      };
    } else {
      return { message: '회원가입에 완료되었습니다.', status: response.data.status };
    }
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return { message: '회원가입에 실패하였습니다.', status: 'error' };
  }
}

/**
 * PIN 체크
 * @param {IPinCheckReqParams} reqData
 * @returns {Promise<string | IResponsePinCheck>}
 */
export async function pinCheck(
  reqData: IPinCheckReqParams,
): Promise<
  | { status: string; message: string; response: IResponsePinCheck }
  | { status: string; message: string; response: IResponsePinCheck }
> {
  try {
    const response = await axios.post(`${checklocal()}/member/verifypin`, reqData);

    if (response.data.status === 'error') {
      return { status: 'error', message: response.data.message, response: response.data };
    } else {
      return { status: 'success', message: response.data.message, response: mapPinCheck(response.data.result) };
    }
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.errorCode.code;
    }

    return { status: 'error', message: 'PIN 체크 실패', response: { success: false } };
  }
}

/**
 * 로그아웃
 * @returns {Promise<string>}
 */
export async function logout(): Promise<string> {
  try {
    const response = await axios.post(
      `${checklocal()}/member/logout`,
      {}, // 빈 객체를 body로 전달
      { withCredentials: true }, // 세션 쿠키를 포함하기 위해 추가
    );

    return response.data.message; // 성공 메시지 반환
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.error || '로그아웃 실패';
    }
    return '로그아웃 실패';
  }
}

/**
 * 모든 로그아웃
 * @returns {Promise<string>}
 */
export async function logoutAll(): Promise<string> {
  try {
    const response = await axios.post(
      `${checklocal()}/member/logoutAll`,
      {}, // 빈 객체를 body로 전달
      { withCredentials: true }, // 세션 쿠키를 포함하기 위해 추가
    );

    return response.data.message; // 성공 메시지 반환
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.error || '로그아웃 실패';
    }
    return '로그아웃 실패';
  }
}

/**
 * 주소 생성
 * @param {string} id
 * @param {string} phone
 * @returns {Promise<string>}
 */
export async function createAddress(id: string, phone: string): Promise<string> {
  try {
    const response = await axios.post(
      `${checklocal()}/wallet/trx/createaddress`,
      { id, phone },
      // { withCredentials: true }, // 세션 쿠키를 포함하기 위해 추가
    );

    return response.data.result;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      return err.response?.data.error || '주소 생성 실패';
    }
    return '주소 생성 실패';
  }
}
